import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { LocalDate, NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { PyhapaivatService } from '@lemon/core/service/pyhapaiva.service'
import { AlvIlmoitusjakso, KirjanpitoKuukausiruudunTilinpaatoksenTila, KirjanpitoKuukausiruutu } from '../model/asiakas'
import { AlvPalluranTila, AlvRajat, EraantymisenTila, TilinpaatoksenKuvake, TilinpaatoksenVaihe } from '../model/tyolistan-yhteenvetokortit'

// Ruudukko ja yhteenvetokortit käyttävät näitä samoja sääntöjä, jotta ne eivät näytä ristiriitaisia tiloja.
export class KuukausiruudunTilatService {
  constructor(
    private _dateService: DateService,
    private _pyhapaivatService: PyhapaivatService,
  ) {}

  // Ruudukon yksinkertaistettu eräpäivä ilman viikonloppu-, pyhä- tai Holvi-siirtoa, jotta kortti ja pallura vaihtavat tilaa samana päivänä.
  public annaAlvRajat(ruutu: KirjanpitoKuukausiruutu, seuraavanRuudunAlv: AlvIlmoitusjakso): AlvRajat {
    const vuosi = 2000 + Math.floor(ruutu.k / 100)
    const kuukausi = ruutu.k % 100
    const onTaysiVuosi = ruutu.alv === AlvIlmoitusjakso.KK12 && kuukausi === 12

    if (onTaysiVuosi) {
      return {
        erapaiva: { year: vuosi + 1, month: 2, day: new Date(vuosi + 1, 2, 0).getDate() },
        sininenJalkeen: new Date(vuosi + 1, 1, 0),
        oranssiJalkeen: new Date(vuosi + 1, 2, -7),
        mustaJalkeen: new Date(vuosi + 1, 2, 0),
      }
    }

    const erapaivanKuukausi = kuukausi + 2
    const erapaiva: LocalDate = erapaivanKuukausi > 12 ? { year: vuosi + 1, month: erapaivanKuukausi - 12, day: 12 } : { year: vuosi, month: erapaivanKuukausi, day: 12 }
    return {
      erapaiva,
      sininenJalkeen: new Date(vuosi, kuukausi, 1),
      oranssiJalkeen: new Date(vuosi, kuukausi + 1, 6),
      mustaJalkeen: new Date(vuosi, kuukausi + 1, 13),
    }
  }

  /**
   * ALV- ja yhteisömyynti-ilmoituksen statuspalluran logiikka.
   * Tämä logiikka on suht monimutkainen niin tässä on rautalangasta väännetty selvitys siitä
   *
   * Käytetyt liput (ks. KirjanpitoKuukausiruutu / asiakas.ts):
   *   bot   ruutu kuuluu tilikaudelle (gateway tähän logiikkaan)
   *   byht  yhteisömyynti-tarve olemassa (rivejä draftissa)
   *   baok  ALV-lähetys OK -override (kirjanpitäjä merkannut)
   *   byok  yhteisömyynti OK -override (HUOM: ei vielä asetettu missään)
   *   balv  ALV-ilmoitus lähetetty ainakin kerran
   *   byhl  yhteisömyynti-ilmoitus lähetetty ainakin kerran
   *   r     ALV tarvitsee uudelleenlähetyksen
   *   ryh   yhteisömyynti tarvitsee uudelleenlähetyksen
   *   u     ruudulla on keskeneräisiä kirjauksia
   *   bl    lukittu (tilikausi merkitty valmiiksi tms.)
   *   kj    ruutu on avatussa kuukaudessa
   *
   * Apumuuttujat:
   *   molemmatOk        = byht ? (baok && byok) : baok
   *   molemmatLahetetty = byht ? (balv && byhl) : balv
   *   tarvitseeUudelleen = (r && balv) || (ryh && byhl)
   *
   * Päähaarat (ensimmäinen täsmäävä voittaa, vain kun bot=true):
   *   1) molemmatOk                                                  -> alvvih   (vihreä, override)
   *   2) !u && tarvitseeUudelleen                                    -> alvpun   (punainen)
   *   3) molemmatLahetetty && !(u && tarvitseeUudelleen)             -> alvvih   (vihreä, lähetetty)
   *   4) !bl && kj                                                   -> erääntymisväri (alla)
   *   -) muu                                                         -> ei ALV-palluraa
   *
   * Skenaariot (bot=T, kj=T, bl=F):
   *   +----+---------------------------------------+------+------+------+------+------+---+----+---+--------------+
   *   | #  | Tilanne                               | byht | baok | byok | balv | byhl | r | ryh| u | -> luokka    |
   *   +----+---------------------------------------+------+------+------+------+------+---+----+---+--------------+
   *   |  1 | Ei mitään lähetetty, vain ALV-tarve   |  F   |  F   |  ·   |  F   |  ·   | F |  F | F | erääntymis   |
   *   |  2 | Ei mitään lähetetty, yht-tarve        |  T   |  F   |  F   |  F   |  F   | F |  F | F | erääntymis   |
   *   |  3 | ALV lähetetty, ei yht-tarvetta        |  F   |  F   |  ·   |  T   |  ·   | F |  F | F | alvvih       |
   *   |  4 | ALV lähetetty, yht-tarve, yht puuttuu |  T   |  F   |  F   |  T   |  F   | F |  F | F | erääntymis   |
   *   |  5 | Yht lähetetty, yht-tarve, ALV puuttuu |  T   |  F   |  F   |  F   |  T   | F |  F | F | erääntymis   |
   *   |  6 | Molemmat lähetetty                    |  T   |  F   |  F   |  T   |  T   | F |  F | F | alvvih       |
   *   |  7 | ALV vaatii uudelleenlähetyksen        |  T   |  F   |  F   |  T   |  T   | T |  F | F | alvpun       |
   *   |  8 | Yht vaatii uudelleenlähetyksen        |  T   |  F   |  F   |  T   |  T   | F |  T | F | alvpun       |
   *   |  9 | Uudelleenlähetys + keskeneräiset      |  T   |  F   |  F   |  T   |  T   | T |  F | T | eräpäiväväri |
   *   | 10 | ALV merkitty OK -overridella          |  F   |  T   |  ·   |  F   |  ·   | F |  F | F | alvvih       |
   *   +----+---------------------------------------+------+------+------+------+------+---+----+---+--------------+
   *
   * Erääntymisväri (haara 4) lasketaan ruutu.k:n vuodesta/kuukaudesta ja ilmoitusjaksosta:
   *   +-------------------+--------+
   *   | Aikaa eräpäivään  | Luokka |
   *   +-------------------+--------+
   *   | erääntynyt        | alvmus |
   *   | <= 1 viikko       | alvora |
   *   | <= 1 kuukausi     | alvsin |
   *   |  > 1 kuukausi     | alvhar |
   *   +-------------------+--------+
   */
  public annaAlvPalluranTila(ruutu: KirjanpitoKuukausiruutu, seuraavanRuudunAlv: AlvIlmoitusjakso, nyt: Date): AlvPalluranTila | null {
    if (!ruutu.bot) {
      return null
    }

    const onYhteisomyyntiTarve = !!ruutu.byht
    const molemmatOk = onYhteisomyyntiTarve ? !!ruutu.baok && !!ruutu.byok : !!ruutu.baok
    const molemmatLahetetty = onYhteisomyyntiTarve ? !!ruutu.balv && !!ruutu.byhl : !!ruutu.balv
    const keskeneraisetKirjaukset = !!ruutu.u
    const tarvitseeUudelleenLahetysta = (!!ruutu.r && !!ruutu.balv) || (!!ruutu.ryh && !!ruutu.byhl)

    if (molemmatOk) {
      return 'vih'
    }
    if (!keskeneraisetKirjaukset && tarvitseeUudelleenLahetysta) {
      return 'pun'
    }
    if (molemmatLahetetty && !(keskeneraisetKirjaukset && tarvitseeUudelleenLahetysta)) {
      return 'vih'
    }
    if (ruutu.bl || !ruutu.kj || !this._onkoEraantymisvariKaytossa(ruutu, seuraavanRuudunAlv)) {
      return null
    }

    const rajat = this.annaAlvRajat(ruutu, seuraavanRuudunAlv)
    if (rajat.mustaJalkeen < nyt) {
      return 'mus'
    }
    if (rajat.oranssiJalkeen < nyt) {
      return 'ora'
    }
    if (rajat.sininenJalkeen < nyt) {
      return 'sin'
    }
    return 'har'
  }

  public annaTilinpaatoksenKuvake(ruutu: KirjanpitoKuukausiruutu): TilinpaatoksenKuvake | null {
    if (ruutu.pp) {
      return null
    }
    switch (ruutu.tpp) {
      case KirjanpitoKuukausiruudunTilinpaatoksenTila.ODOTTAA_TILINPAATOKSEN_ALLEKIRJOITTAMISTA:
        return 'sinikyna'
      case KirjanpitoKuukausiruudunTilinpaatoksenTila.ODOTTAA_TILINTARKASTUSTA:
        return 'sinisuurennuslasi'
      case KirjanpitoKuukausiruudunTilinpaatoksenTila.ODOTTAA_YHTIOKOKOUKSEN_POYTAKIRJAN_ALLEKIRJOITTAMISTA:
        return 'sininuija'
      case KirjanpitoKuukausiruudunTilinpaatoksenTila.ODOTTAA_REKISTEROINTIA:
        return 'oranssikyna'
      default:
        return null
    }
  }

  public annaTilinpaatoksenVaihe(ruutu: KirjanpitoKuukausiruutu, yritysmuoto: Yritysmuoto): TilinpaatoksenVaihe | null {
    if (ruutu.pp || ruutu.tpp === KirjanpitoKuukausiruudunTilinpaatoksenTila.VALMIS) {
      return null
    }
    switch (ruutu.tpp) {
      case KirjanpitoKuukausiruudunTilinpaatoksenTila.ODOTTAA_TILINPAATOKSEN_ALLEKIRJOITTAMISTA:
      case KirjanpitoKuukausiruudunTilinpaatoksenTila.ODOTTAA_TILINTARKASTUSTA:
      case KirjanpitoKuukausiruudunTilinpaatoksenTila.ODOTTAA_YHTIOKOKOUKSEN_POYTAKIRJAN_ALLEKIRJOITTAMISTA:
        return 'allekirjoittamatta'
      case KirjanpitoKuukausiruudunTilinpaatoksenTila.ODOTTAA_REKISTEROINTIA:
        return 'rekisteroimatta'
    }
    const onTilinpaatosvelvollinen = yritysmuoto === Yritysmuoto.OSAKEYHTIO || yritysmuoto === Yritysmuoto.OSUUSKUNTA
    if (!ruutu.tpp && !ruutu.tp && !ruutu.bd && onTilinpaatosvelvollinen) {
      return 'tekematta'
    }
    return null
  }

  // Sama sääntö kuin valmiusprosentissa, mutta prosentti laskee kaikki kirjanpitäjän asiakkaat, joten luvut eivät täsmää.
  public onkoKirjanpitoTehtavissa(ruutu: KirjanpitoKuukausiruutu, onkoHolvi: boolean): boolean {
    return !ruutu.bd && !!ruutu.bot && (onkoHolvi || (!ruutu.po && !ruutu.t && !!ruutu.sv && !!ruutu.h))
  }

  public annaTilikaudenLoppu(ruutu: KirjanpitoKuukausiruutu): LocalDate | null {
    if (!ruutu.l) {
      return null
    }
    return { year: 2000 + Math.floor(ruutu.k / 100), month: ruutu.k % 100, day: ruutu.l }
  }

  // Tmi ja yhtymät ilmoittavat tilikauden päättymisvuoden verovuotena seuraavana keväänä, yhteisöt 4 kk päättymiskuukauden lopusta (Verohallinto).
  public annaVeroilmoituksenErapaiva(yritysmuoto: Yritysmuoto, loppu: LocalDate): LocalDate {
    const onHenkilovero = yritysmuoto === Yritysmuoto.TOIMINIMI || yritysmuoto === Yritysmuoto.AVOINYHTIO || yritysmuoto === Yritysmuoto.KOMMANDIITTIYHTIO
    if (onHenkilovero) {
      return { year: loppu.year + 1, month: 4, day: 1 }
    }
    return this._dateService.kuukaudenViimeinenPaikallinen(this._dateService.lisaaKuukausiaPaikallinen({ ...loppu, day: 1 }, 4))
  }

  public annaRekisteroinninErapaiva(loppu: LocalDate): LocalDate {
    return this._lisaaKuukausiaSailyttaenKuunViimeinen(loppu, 8)
  }

  public annaOsinkoilmoituksenErapaiva(nostopaiva: NumberDate | undefined): LocalDate | null {
    if (!nostopaiva) {
      return null
    }
    const nostettu = this._dateService.numberToLocalDate(nostopaiva)
    const seuraavaKuukausi = this._dateService.lisaaKuukausiaPaikallinen({ year: nostettu.year, month: nostettu.month, day: 1 }, 1)
    const kahdestoista = this._dateService.localDateToDate({ ...seuraavaKuukausi, day: 12 })
    return this._dateService.dateToLocalDate(this._pyhapaivatService.annaSeuraavaArkipaivaJosOnPyhaTaiViikonloppu(kahdestoista))
  }

  public annaEraantymisenTila(erapaiva: LocalDate, tanaan: LocalDate): EraantymisenTila {
    if (this._dateService.compareLocalDates(erapaiva, '<', tanaan)) {
      return 'myohassa'
    }
    if (this._dateService.compareLocalDates(erapaiva, '<=', this._dateService.lisaaPaiviaPaikallinen(tanaan, 1))) {
      return 'eraantyy'
    }
    return 'normaali'
  }

  private _lisaaKuukausiaSailyttaenKuunViimeinen(pvm: LocalDate, kuukausia: number): LocalDate {
    const lisatty = this._dateService.lisaaKuukausiaPaikallinen(pvm, kuukausia)
    const onKuunViimeinen = this._dateService.compareLocalDates(pvm, '==', this._dateService.kuukaudenViimeinenPaikallinen(pvm))
    return onKuunViimeinen ? this._dateService.kuukaudenViimeinenPaikallinen(lisatty) : lisatty
  }

  private _onkoEraantymisvariKaytossa(ruutu: KirjanpitoKuukausiruutu, seuraavanRuudunAlv: AlvIlmoitusjakso) {
    const kuukausi = ruutu.k % 100
    switch (ruutu.alv) {
      case AlvIlmoitusjakso.KK1:
        return true
      case AlvIlmoitusjakso.KK3:
        return kuukausi % 3 === 0 || seuraavanRuudunAlv !== AlvIlmoitusjakso.KK3
      case AlvIlmoitusjakso.KK12:
        return kuukausi === 12 || seuraavanRuudunAlv !== AlvIlmoitusjakso.KK12
      default:
        return false
    }
  }
}
